import { GeoJSON } from 'ol/format';
import {
  GeoJSONLineString,
  GeoJSONMultiPolygon,
  GeoJSONPoint,
  GeoJSONPolygon,
} from 'ol/format/GeoJSON';
import Geometry from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import * as olProj from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import { FenceZone } from '../../../util/enums';
import { GeofencePoint, SimpleGeofence } from '../../../util/Events/Messages';
import { StrongFeatureHolder } from '../../../hooks/geomoby/useLiveMapLoader';
import { MICROFENCE_LAYER_ID, MICROFENCE_LAYER_LABEL } from '../../../util/constants';
import { Microfence } from '../../../API/types/triggers';

export const gpsToMap = olProj.getTransformFromProjections(
  olProj.get('EPSG:4326'),
  olProj.get('EPSG:3857'),
);
export const mapToGPS = olProj.getTransformFromProjections(
  olProj.get('EPSG:3857'),
  olProj.get('EPSG:4326'),
);

export interface LayerResult {
  lid: string;
  name: string;
  layer: StrongFeatureHolder<Polygon, SimpleGeofence>[];
}

export function featuresToLayer(
  layerId: string,
  layerName: string,
  features: {
    points: GeoJSONPolygon | GeoJSONLineString | GeoJSONPoint;
    id: string;
    name: string;
    zone?: FenceZone;
  }[],
): LayerResult {
  const layer: StrongFeatureHolder<Polygon, SimpleGeofence>[] = features.map(jsonFeature =>
    jsonTofeature(layerId, layerName, jsonFeature),
  );
  return {
    lid: layerId,
    name: layerName,
    layer: layer,
  };
}

export function layerToJson(layerSource: VectorSource<Geometry>) {
  const copied = layerSource
    .getFeatures()
    .map(f => f.clone())
    .flatMap(f => f.get('features') ?? f);
  copied.forEach(f => f.getGeometry()?.applyTransform(mapToGPS));
  return new GeoJSON().writeFeaturesObject(copied, {
    featureProjection: 'EPSG:4326',
  });
}

function jsonTofeature(
  layerId: string,
  layerName: string,
  jsonFeature: {
    points: GeoJSONPolygon | GeoJSONLineString | GeoJSONMultiPolygon;
    id: string;
    name: string;
    zone?: FenceZone;
  },
): StrongFeatureHolder<Polygon, SimpleGeofence> {
  const feature = new GeoJSON().readFeature(
    {
      type: 'Feature',
      geometry: jsonFeature.points,
      properties: {
        ...jsonFeature,
        layerId,
        layerName,
      },
    },
    {
      featureProjection: 'EPSG:4326',
    },
  );
  feature.getGeometry().applyTransform(gpsToMap);
  return {
    data: {
      ...jsonFeature,
      layerId,
      layerName,
    } as unknown as SimpleGeofence,
    feature,
  };
}

export function microfencesToMicrofenceCluster(
  data: Microfence[],
): StrongFeatureHolder<Point, Microfence>[] {
  return data
    .filter(m => !!m.point)
    .map((microfence: Microfence) => {
      const feature = new GeoJSON().readFeature(microfence.point, {
        featureProjection: 'EPSG:4326',
      });
      feature.getGeometry().applyTransform(gpsToMap);
      const microfenceData: Microfence = {
        ...microfence,
        layerId: MICROFENCE_LAYER_ID,
        layerName: MICROFENCE_LAYER_LABEL,
      };
      feature.setProperties(microfenceData);
      return {
        data: microfenceData,
        feature,
      };
    });
}

export function jsonToPointCluster(
  geofencePoints: GeofencePoint[],
): StrongFeatureHolder<Point, SimpleGeofence>[] {
  return geofencePoints
    .filter(geofencePoint => !!geofencePoint.point)
    .map(geofencePoint => {
      const feature = new GeoJSON().readFeature(geofencePoint.point, {
        featureProjection: 'EPSG:4326',
      });
      feature.getGeometry().applyTransform(gpsToMap);
      return {
        data: geofencePoint.geofence,
        feature,
      };
    });
}
